import { InvestorDetailsEntityTypeEnum } from '../investorDetailsPage/types';
import {
  EditInvestorAddInvestorEntityInvestorHouseholdOptionEnum,
  EditInvestorAllocationSettingsAlwaysShowOptionEnum,
  EditInvestorAllocationSettingsBiteSizeOptionEnum,
  EditInvestorAllocationSettingsOnHoldOptionEnum,
  EditInvestorAllocationTargetsAssetClassTargetsOptionEnum,
  EditInvestorAllocationTargetsCapacityCalculationOptionEnum,
  EditInvestorEntityDetailsTaxStatusOptionEnum,
  EditInvestorHouseholdClientAdvisorOptionEnum,
  EditInvestorSectionNameEnum
} from './types';

/**
 * Naming convention for option constants:
 * editInvestor[section/modalPageName][optionName]Options
 *
 * @format editInvestor{sectionName}{optionName}Options
 *
 * @examples
 * - editInvestorHouseholdClientAdvisorOptions (household section, client advisor option)
 * - editInvestorAllocationTargetsCapacityCalculationOptions (allocation targets section, capacity calculation option)
 * - editInvestorEntityDetailsTaxStatusOptions (entity details section, tax status option)
 */

export const editInvestorHouseholdClientAdvisorOptions = Object.values(EditInvestorHouseholdClientAdvisorOptionEnum);

export const editInvestorAddInvestorEntityInvestorHouseholdOptions = Object.values(EditInvestorAddInvestorEntityInvestorHouseholdOptionEnum);
export const editInvestorEntityDetailsTaxStatusOptions = Object.values(EditInvestorEntityDetailsTaxStatusOptionEnum);

export const editInvestorAllocationTargetsTotalEntityValueTooltips = {
  [InvestorDetailsEntityTypeEnum.Household]: '',
  [InvestorDetailsEntityTypeEnum.Group]:
    'This amount reflects the total value of all entities contained in this group.  Edit each grouped entity to update this amount.',
  [InvestorDetailsEntityTypeEnum.GroupedEntity]:
    'The total investable assets for this entity. This value is used to calculate target allocation amounts.',
  [InvestorDetailsEntityTypeEnum.UngroupedEntity]:
    'The total investable assets for this entity. This value is used to calculate target allocation amounts.'
};

export const editInvestorAllocationTargetsCapacityCalculationOptions = Object.values(EditInvestorAllocationTargetsCapacityCalculationOptionEnum);
export const editInvestorAllocationTargetsCapacityCalculationBannerDescriptions = {
  [EditInvestorAllocationTargetsCapacityCalculationOptionEnum.DisregardAllocationTargets]:
    'When you provide Allocation Targets for this entity capacity calculations and allocation suitability checks  are made available.',
  [EditInvestorAllocationTargetsCapacityCalculationOptionEnum.PacingModel]:
    'Create a multi-year commitment plan that builds up to private capital allocation with a total Net Asset Value equal to the above targets in each asset class.  The commitment plan is based on forecasted capital calls and distributions.  The timing of new commitments is calculated to achieve and maintain the targeted Net Asset Value in each asset class.',
  [EditInvestorAllocationTargetsCapacityCalculationOptionEnum.OverCommitmentRatio]:
    'Capacity is  calculated by deducting Economic Exposure, (the current value of existing commitments plus the  corresponding unfunded commitment) from the investor entity’s private capital target (Total Entity Value x private capital percentage).  Use the Over Commitment Ratio to increase capacity for more aggressive portfolios.'
};

export const editInvestorAllocationTargetsAssetClassTargetsOptions = Object.values(EditInvestorAllocationTargetsAssetClassTargetsOptionEnum);
export const editInvestorAllocationTargetsAssetClassTargetsBannerDescription =
  'By default all new investor entities use the same default asset class targets.';

export const editInvestorAllocationSettingsOnHoldOptions = Object.values(EditInvestorAllocationSettingsOnHoldOptionEnum);

export const editInvestorAllocationSettingsAlwaysShowOptions = Object.values(EditInvestorAllocationSettingsAlwaysShowOptionEnum);
export const editInvestorAllocationSettingsAlwaysShowBannerDescription =
  'Indicate whether this investor will review only those opportunities that are recommended based on the needs of the portfolio, or if the investor will see all opportunities (both recommended and not recommended).';

export const editInvestorAllocationSettingsBiteSizeOptions = Object.values(EditInvestorAllocationSettingsBiteSizeOptionEnum);
export const editInvestorAllocationSettingsBiteSizeBannerDescription =
  'The commitment amount will be set to the minimum required for any deal unless  a specific default bite size is set here. This amount can be overridden when making an allocation.';

export const editInvestorSections: Record<InvestorDetailsEntityTypeEnum, EditInvestorSectionNameEnum[]> = {
  [InvestorDetailsEntityTypeEnum.Household]: [],
  [InvestorDetailsEntityTypeEnum.Group]: [EditInvestorSectionNameEnum.AllocationTargets, EditInvestorSectionNameEnum.AllocationSettings],
  [InvestorDetailsEntityTypeEnum.GroupedEntity]: [
    EditInvestorSectionNameEnum.EntityDetails,
    EditInvestorSectionNameEnum.AllocationTargets,
    EditInvestorSectionNameEnum.DataIntegration
  ],
  [InvestorDetailsEntityTypeEnum.UngroupedEntity]: [
    EditInvestorSectionNameEnum.EntityDetails,
    EditInvestorSectionNameEnum.AllocationTargets,
    EditInvestorSectionNameEnum.AllocationSettings,
    EditInvestorSectionNameEnum.DataIntegration
  ]
} as const;
